/**
 * Module dependencies.
 */

import { Button, icons } from 'src/components/core';
import { FavoriteProps } from 'src/types/my-products';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { authRoutes } from 'src/core/routes';
import { media, units } from '@untile/react-components';
import { useAuth } from 'src/context/auth/context';
import { useFavoritesContext } from 'src/context/favorites/context';
import { useRouter } from 'next/router';
import RemoveFavoriteModal from 'src/components/my-products/favorites/remove-favorite-modal';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  contactUrl: string;
  customizationUrl?: string;
  existingFavorite: FavoriteProps | null;
  hasCustomizationsButton?: boolean;
  id: string;
  onGoToModelRangesSection?: () => void;
  refetchExistingFavorite: () => void;
  slug?: string;
  type: 'modelRange' | 'technology';
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: ${units(3)} 0;

  ${media.min('md')`
    align-items: center;
    display: grid;
    grid-gap: ${units(3)};
    grid-template-areas:
      'contact    modelRanges'
      'myProducts myProducts';
    grid-template-columns: max-content 1fr;
  `}
`;

/**
 * `ContactButton` styled component.
 */

const ContactButton = styled(Button)`
  grid-area: contact;
`;

/**
 * `ModelRangesButton` styled component.
 */

const ModelRangesButton = styled(Button)`
  grid-area: modelRanges;
`;

/**
 * `MyProductsButtonsWrapper` styled component.
 */

const MyProductsButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: myProducts;
  grid-gap: ${units(3)};

  ${media.min('sm')`
    flex-direction: row;
  `}

  ${media.min('md')`
    flex-direction: column;
  `}

  ${media.min('xl')`
    flex-direction: row;
  `}
`;

/**
 * `ButtonsWrapper` component.
 */

const ButtonsWrapper = (props: Props): ReactElement => {
  const {
    className,
    contactUrl,
    existingFavorite,
    id,
    onGoToModelRangesSection,
    refetchExistingFavorite
  } = props;

  const { isSubmitting, onAddFavorite, onRemoveFavorite } =
    useFavoritesContext();

  const router = useRouter();
  const { translate } = useTranslate();
  const { isAuthenticated } = useAuth();
  const [isModalOpen, setModalOpen] = useState<boolean>();
  const isFavorited = useMemo(() => {
    return !isEmpty(existingFavorite);
  }, [existingFavorite]);

  const handleRemoveFavorite = useCallback(() => {
    onRemoveFavorite(existingFavorite.favorite.id).then(() => {
      setModalOpen(false);
      refetchExistingFavorite();
    });
  }, [
    existingFavorite?.favorite?.id,
    onRemoveFavorite,
    refetchExistingFavorite
  ]);

  const handleClickFavorite = useCallback(() => {
    if (!isAuthenticated) {
      window.localStorage.setItem('favorite', id);
      router.push(authRoutes.signIn);

      return;
    }

    if (isFavorited) {
      setModalOpen(true);

      return;
    }

    onAddFavorite(id).then(() => refetchExistingFavorite());
  }, [
    id,
    isAuthenticated,
    isFavorited,
    onAddFavorite,
    refetchExistingFavorite,
    router
  ]);

  return (
    <Wrapper className={className}>
      <ContactButton href={contactUrl}>
        {translate('common:actions.requestForFurtherInfo')}
      </ContactButton>

      <MyProductsButtonsWrapper>
        <Button
          colorTheme={'secondary'}
          disabled={isSubmitting}
          icon={isFavorited ? icons.heartFull : icons.heart}
          isReversed
          onClick={handleClickFavorite}
        >
          {isFavorited
            ? translate('common:actions.deleteFavourites')
            : translate('common:actions.addFavourites')}
        </Button>

        <RemoveFavoriteModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          onRemoveFavorite={handleRemoveFavorite}
        />
      </MyProductsButtonsWrapper>

      {onGoToModelRangesSection && (
        <ModelRangesButton
          colorTheme={'tertiary'}
          onClick={onGoToModelRangesSection}
          size={'small'}
        >
          {translate('common:actions.viewModelRanges')}
        </ModelRangesButton>
      )}
    </Wrapper>
  );
};

/**
 * Export `ButtonsWrapper` component.
 */

export default ButtonsWrapper;
